import { useParams } from 'next/navigation';
import { CREATE_CONVERSATION_AUTH } from 'schema-v2/Chat/mutations';
import { Channel, ChannelFilters, StreamChat } from 'stream-chat';
import { Chat, useChatContext, useCreateChatClient } from 'stream-chat-react';
import { CHANNEL_TYPE, UNIFIED_GRAPH } from 'utils/constants';
import { conversationFilter } from 'utils/new/conversationFilter';
import { rawId } from 'utils/rawId';

import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { useMutation } from '@apollo/client';

import { useCommunityContext } from 'components/CommunityContext';
import PageSkeleton from 'components/PageSkeleton';

const StreamContextMapper = ({
  children,
}: {
  children: ({
    session,
    setActiveConversation,
    currentConversation,
    hasUnreadMessages,
    findExistingConversation,
    baseChannelFilters,
    conversations,
  }: {
    session: StreamChat;
    setActiveConversation: (conversation: Channel) => void;
    currentConversation: Channel | null;
    hasUnreadMessages: boolean;
    findExistingConversation: (userIds: string[]) => Promise<Channel | null>;
    baseChannelFilters: ChannelFilters;
    conversations: Channel[];
  }) => ReactNode;
}) => {
  const { client, setActiveChannel } = useChatContext();
  const { community } = useCommunityContext();
  const { conversationId } = useParams();
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const [conversations, setConversations] = useState<Channel[]>([]);

  const currentConversation = useMemo(() => {
    if (!conversationId) return null;
    return client?.getChannelById(CHANNEL_TYPE, conversationId as string, {
      community_id: rawId(community.id)?.toString() || '',
    });
  }, [client, conversationId, community.id]);

  useEffect(() => {
    const setupChannel = async () => {
      if (!currentConversation) return;
      await currentConversation.watch();
      setActiveChannel?.(currentConversation);
    };
    setupChannel();

    return () => {
      setActiveChannel?.(undefined);
    };
  }, [currentConversation]);

  const baseChannelFilters = useMemo(() => {
    return {
      type: CHANNEL_TYPE,
      team: client?.user?.teams?.[0],
      community_id: rawId(community.id)?.toString(),
    };
  }, [client, community.id]);

  useEffect(() => {
    if (!client) return undefined;
    // Initialize unread count from client user data
    // @ts-ignore - Stream Chat types don't include total_unread_count yet
    if (client?.user?.total_unread_count) {
      // @ts-ignore
      setHasUnreadMessages(client?.user?.total_unread_count > 0);
    }

    const updateUnreadStatus = async () => {
      const unreadChannels = await client.queryChannels(
        {
          ...baseChannelFilters,
          // @ts-ignore
          has_unread: true,
        },
        {},
        { limit: 1 }
      );

      setHasUnreadMessages(unreadChannels?.length > 0 || false);
    };

    const fetchConversations = async () => {
      const channelResponse = await client.queryChannels({
        ...baseChannelFilters,
        members: { $in: [client.user?.id as string] },
      });

      const filteredConversations = channelResponse.filter((channel) =>
        conversationFilter(channel, baseChannelFilters, client)
      );

      setConversations(filteredConversations);
    };

    const handleMessageEvents = async () => {
      await updateUnreadStatus();

      if (conversations.length === 0) {
        await fetchConversations();
      }
    };

    fetchConversations();

    client.on('message.new', handleMessageEvents);
    client.on('notification.message_new', handleMessageEvents);
    client.on('message.read', updateUnreadStatus);
    client.on('notification.added_to_channel', fetchConversations);

    return () => {
      client.off('message.new', handleMessageEvents);
      client.off('notification.message_new', handleMessageEvents);
      client.off('message.read', updateUnreadStatus);
      client.off('notification.added_to_channel', fetchConversations);
    };
  }, [client]);

  // TODO: we will move this logic to API service, which will return channel id when returning conversation exists error
  const findExistingConversation = useCallback(
    async (userIds: string[]) => {
      if (!client) return null;
      const channels = await client?.queryChannels(
        {
          ...baseChannelFilters,
          members: { $eq: userIds },
        },
        {},
        { limit: 1 }
      );
      return channels?.[0];
    },
    [client, community.id]
  );

  return (
    <>
      {children({
        session: client,
        setActiveConversation: setActiveChannel,
        currentConversation,
        hasUnreadMessages,
        findExistingConversation,
        baseChannelFilters,
        conversations,
      })}
    </>
  );
};

export const StreamSession = ({ children }: { children: ReactNode }) => {
  const { currentUser } = useCommunityContext();
  const [createConversationAuth, { error: authError, loading: authLoading }] = useMutation(
    CREATE_CONVERSATION_AUTH,
    {
      context: { clientName: UNIFIED_GRAPH },
    }
  );

  const getTokenProvider = useCallback(async () => {
    const { data } = await createConversationAuth();
    if (!data?.createConversationAuth?.auth?.token) {
      throw new Error('Something went wrong.');
    }
    return data.createConversationAuth.auth.token;
  }, [createConversationAuth]);

  const client = useCreateChatClient({
    apiKey: process.env.STREAM_CHAT_API_KEY || '',
    tokenOrProvider: getTokenProvider,
    userData: {
      id: currentUser?.gid,
      name: currentUser?.fullName,
      image: currentUser?.avatarUrl,
    },
  });

  if (authLoading) {
    return <PageSkeleton />;
  }

  if (authError) {
    return <>{children}</>;
  }

  if (!client) {
    return null;
  }

  return (
    <Chat
      client={client}
      customClasses={{
        channelList: 'str-chat__channel-list-custom',
        channel: 'str-chat__channel-custom',
      }}
    >
      {children}
    </Chat>
  );
};

export default function StreamSessionProvider({ children }) {
  return (
    <StreamSession>
      <StreamContextMapper>{children}</StreamContextMapper>
    </StreamSession>
  );
}
